<script setup lang="ts">
// NPM
import { ref } from 'vue';
import { useMq } from 'vue3-mq';

// Components
import {
  BaseCard,
  BaseEmptyState,
  Modal,
  BaseButton,
  BaseBadge,
} from '@magma-app/magma-lapilli';

// API
import { useUgcs } from '@/api/ugcs/use-ugcs';
import { useActionDeepLink } from '@/composables/useActionDeepLink';

// Plugins
const mq = useMq();

const { data: ugcs, isLoading } = useUgcs();

const modal = ref<number | null>(null);

// Opens one action's modal directly from a ?ugcId= deep link.
useActionDeepLink({
  type: 'ugc',
  bucket: 'validated',
  onOpen: (_item, id) => {
    modal.value = id;
  },
});

const getUgcImgUrl = (ugc: { pictureUrl?: string | null }) =>
  ugc.pictureUrl
    ? ugc.pictureUrl
    : mq.lgPlus
      ? '/img/ugc_lg.png'
      : '/img/ugc.png';
</script>

<template>
  <div
    v-if="!isLoading"
    class="block h-full flex-1 px-4 md:px-8"
    :class="{
      'content-start lg:flex lg:flex-wrap': ugcs?.validated.length
    }"
  >
    <template v-if="ugcs?.validated.length">
      <Modal
        v-for="ugc in ugcs?.validated"
        :key="ugc.userUgcId"
        :open="modal === ugc.ugcId"
        content-class="max-w-[90dvw] sm:max-w-[480px]"
        @open-focus="(e) => e.preventDefault()"
        @open-change="
          (open) => {
            modal = ugc.ugcId;

            if (!open) {
              modal = null;
            }
          }
        "
      >
        <template #trigger>
          <BaseCard
            aria-hidden="true"
            class="mb-2 lg:mb-6 lg:mr-6"
            :title="ugc.name"
            :description="ugc.description"
            :img-url="getUgcImgUrl(ugc)"
            :points="ugc.points"
            :badge-status="{ points: ugc.points, type: 'ugc' }"
            :display-points="!ugc.pictureUrl"
          />
        </template>
        <template #content>
          <div>
            <img
              :src="getUgcImgUrl(ugc)"
              :alt="ugc.name"
              class="h-[200px] w-full rounded-[5px] object-cover mb-5"
            />
            <h3 class="text-mgm-txt-lg font-semibold text-foreground-emphasis">
              {{ ugc.name }}
            </h3>
            <BaseBadge
              class="mt-2"
              size="md"
              :text="ugc.points"
              icon-type="icon"
              icon-name="star"
            />
            <div
              class="rich-text-content text-mgm-txt-sm text-foreground-default my-5 max-h-[40dvh] lg:max-h-auto overflow-y-auto"
              v-html="ugc.description"
            ></div>
            <BaseButton
              size="lg"
              disabled
              class="!bg-background-success-default w-full text-foreground-success-default border-none"
              >{{ $t('ugcs.validated.cta') }}</BaseButton
            >
          </div>
        </template>
      </Modal>
    </template>

    <BaseEmptyState
      v-else
      class="flex h-full flex-col items-center justify-center"
      :text="$t('ugcsView.empty.all')"
      type="empty-list"
    />
  </div>
</template>
